import { useTranslation } from 'react-i18next';
import {
  Container,
  Grid,
  Box,
  Typography,
  Card,
  CardContent,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PsychologyIcon from '@mui/icons-material/Psychology';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import ShieldIcon from '@mui/icons-material/Shield';
import StarIcon from '@mui/icons-material/Star';
import GroupsIcon from '@mui/icons-material/Groups';
import FlagIcon from '@mui/icons-material/Flag';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PublicIcon from '@mui/icons-material/Public';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PersonIcon from '@mui/icons-material/Person';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import EmailIcon from '@mui/icons-material/Email';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import {
  Section,
  PageHero,
  SectionTitle,
  AnimatedBox,
  GradientButton,
} from '../../components/ui';

const MissionVisionCard = styled(Card)(({ theme }) => ({
  height: '100%',
  background: '#FFFFFF',
  border: '1px solid rgba(0, 0, 0, 0.06)',
  position: 'relative',
  overflow: 'hidden',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: theme.palette.accent.gradient,
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: '72px',
  height: '72px',
  borderRadius: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(91, 141, 239, 0.1) 100%)',
  marginBottom: theme.spacing(3),

  '& svg': {
    fontSize: '32px',
    color: theme.palette.primary.main,
  },
}));

const ValueCard = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(4, 3),
  borderRadius: '16px',
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  transition: 'all 0.3s ease',

  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    transform: 'translateY(-4px)',
  },
}));

const ValueIcon = styled(Box)(({ theme }) => ({
  width: '56px',
  height: '56px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: theme.palette.accent.gradient,
  margin: '0 auto',
  marginBottom: theme.spacing(2),

  '& svg': {
    fontSize: '24px',
    color: '#FFFFFF',
  },
}));

const TimelineContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(4, 0),

  '&::before': {
    content: '""',
    position: 'absolute',
    left: '50%',
    top: 0,
    bottom: 0,
    width: '2px',
    background: `linear-gradient(180deg,
      ${theme.palette.primary.main} 0%,
      ${theme.palette.secondary.main} 100%)`,
    transform: 'translateX(-50%)',

    [theme.breakpoints.down('md')]: {
      left: '32px',
    },
  },
}));

const TimelineItem = styled(Box)(({ theme, align }) => ({
  display: 'flex',
  justifyContent: align === 'right' ? 'flex-start' : 'flex-end',
  paddingRight: align === 'right' ? 0 : '50%',
  paddingLeft: align === 'right' ? '50%' : 0,
  position: 'relative',
  marginBottom: theme.spacing(6),

  [theme.breakpoints.down('md')]: {
    paddingLeft: theme.spacing(10),
    paddingRight: 0,
    justifyContent: 'flex-start',
  },
}));

const TimelineContent = styled(Card)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(3),
  background: '#FFFFFF',
  border: '1px solid rgba(0, 0, 0, 0.08)',
  borderRadius: '16px',
  maxWidth: '450px',
  width: '100%',
  transition: 'all 0.3s ease',
  textAlign: 'left',

  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 40px rgba(0, 212, 255, 0.15)',
    borderColor: theme.palette.primary.main,
  },

  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

const TimelineDot = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: '50%',
  top: '20px',
  width: '64px',
  height: '64px',
  borderRadius: '50%',
  background: theme.palette.accent.gradient,
  transform: 'translateX(-50%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 4px 20px rgba(0, 212, 255, 0.3)',
  zIndex: 2,
  border: '4px solid #FFFFFF',

  '& svg': {
    fontSize: '28px',
    color: '#FFFFFF',
  },

  [theme.breakpoints.down('md')]: {
    left: '32px',
    width: '48px',
    height: '48px',

    '& svg': {
      fontSize: '22px',
    },
  },
}));

const YearBadge = styled(Box)(({ theme }) => ({
  display: 'inline-block',
  padding: theme.spacing(0.75, 2.5),
  borderRadius: '20px',
  background: theme.palette.accent.gradient,
  color: '#FFFFFF',
  fontWeight: 700,
  fontSize: '1rem',
  marginBottom: theme.spacing(1.5),
  boxShadow: '0 2px 10px rgba(0, 212, 255, 0.2)',
}));

const DirectorMessageCard = styled(Card)(({ theme }) => ({
  background: '#FFFFFF',
  border: '1px solid rgba(0, 0, 0, 0.06)',
  position: 'relative',
  overflow: 'hidden',
  maxWidth: '1000px',
  margin: '0 auto',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: theme.palette.accent.gradient,
  },
}));

const QuoteIcon = styled(Box)(({ theme }) => ({
  width: '80px',
  height: '80px',
  borderRadius: '50%',
  background: theme.palette.accent.gradient,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto',
  marginTop: '-50px',
  marginBottom: theme.spacing(2),
  boxShadow: '0 8px 24px rgba(0, 212, 255, 0.3)',
  position: 'relative',
  zIndex: 2,

  '& svg': {
    fontSize: '40px',
    color: '#FFFFFF',
  },
}));

const AuthorBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: theme.spacing(0.5),
  paddingTop: theme.spacing(3),
  marginTop: theme.spacing(4),
}));

const SignatureName = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'inline-block',
  paddingBottom: theme.spacing(1),

  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: theme.palette.accent.gradient,
    borderRadius: '2px',
  },
}));

const AuthorAvatar = styled(Box)(({ theme }) => ({
  width: '56px',
  height: '56px',
  borderRadius: '50%',
  background: theme.palette.accent.gradient,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '3px solid #FFFFFF',
  boxShadow: '0 4px 12px rgba(0, 212, 255, 0.2)',

  '& svg': {
    fontSize: '28px',
    color: '#FFFFFF',
  },
}));

const DirectorCard = styled(Card)(({ theme }) => ({
  background: '#FFFFFF',
  borderRadius: '24px',
  overflow: 'hidden',
  border: '1px solid rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s ease',
  maxWidth: '800px',
  margin: '0 auto',

  '&:hover': {
    boxShadow: '0 20px 60px rgba(0, 212, 255, 0.15)',
    transform: 'translateY(-4px)',
  },
}));

const DirectorHeader = styled(Box)(({ theme }) => ({
  background: theme.palette.accent.gradient,
  padding: theme.spacing(4),
  position: 'relative',
  overflow: 'hidden',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    right: '-20%',
    width: '400px',
    height: '400px',
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.1)',
  },
}));

const AvatarCircle = styled(Box)(({ theme }) => ({
  width: '120px',
  height: '120px',
  borderRadius: '50%',
  background: 'rgba(255, 255, 255, 0.2)',
  border: '4px solid #FFFFFF',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto',
  marginBottom: theme.spacing(2),
  position: 'relative',
  zIndex: 1,

  '& svg': {
    fontSize: '60px',
    color: '#FFFFFF',
  },

  [theme.breakpoints.down('sm')]: {
    width: '100px',
    height: '100px',

    '& svg': {
      fontSize: '50px',
    },
  },
}));

const values = [
  { key: 'innovation', icon: <LightbulbIcon /> },
  { key: 'integrity', icon: <ShieldIcon /> },
  { key: 'excellence', icon: <StarIcon /> },
  { key: 'collaboration', icon: <GroupsIcon /> },
];

const milestoneIcons = {
  '2019': <FlagIcon />,
  '2020': <TrendingUpIcon />,
  '2021': <BusinessCenterIcon />,
  '2022': <EmojiEventsIcon />,
  '2023': <PublicIcon />,
  '2024': <AutoAwesomeIcon />,
};

export default function About() {
  const { t } = useTranslation();

  return (
    <>
      {/* Page Header */}
      <PageHero title={t('about.title')} subtitle={t('about.subtitle')} />

      {/* Message from Director */}
      <Section>
        <Container maxWidth="lg">
          <AnimatedBox animation="fadeInUp">
            <DirectorMessageCard>
              <CardContent sx={{ p: { xs: 4, md: 6 } }}>
                <Typography
                  variant="overline"
                  sx={{
                    color: 'primary.main',
                    fontWeight: 600,
                    letterSpacing: '0.15em',
                    display: 'block',
                    textAlign: 'center',
                    mb: 3,
                  }}
                >
                  {t('about.director.tagline')}
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { xs: '1.125rem', md: '1.25rem' },
                    lineHeight: 1.9,
                    color: 'text.secondary',
                    mb: 3,
                    textAlign: 'left',
                  }}
                >
                  {t('about.director.intro')}
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { xs: '1.125rem', md: '1.25rem' },
                    lineHeight: 1.9,
                    color: 'text.primary',
                    fontWeight: 600,
                    textAlign: 'left',
                    fontStyle: 'italic',
                  }}
                >
                  "{t('about.director.promise')}"
                </Typography>

                <AuthorBox>
                  <SignatureName>
                    <Typography
                      variant="h6"
                      sx={{
                        color: 'text.primary',
                        fontWeight: 700,
                      }}
                    >
                      {t('about.director.author').split(',')[0]}
                    </Typography>
                  </SignatureName>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 500,
                    }}
                  >
                    {t('about.director.author').split(',')[1]?.trim() || 'Director'}
                  </Typography>
                </AuthorBox>
              </CardContent>
            </DirectorMessageCard>
          </AnimatedBox>
        </Container>
      </Section>

      {/* Our Journey */}
      <Section variant="light">
        <Container maxWidth="lg">
          <SectionTitle
            title={t('about.journey.title')}
            subtitle={t('about.journey.subtitle')}
          />

          <TimelineContainer>
            {['2019', '2020', '2021', '2022', '2023', '2024'].map((year, index) => (
              <TimelineItem key={year} align={index % 2 === 0 ? 'left' : 'right'}>
                <TimelineDot>
                  {milestoneIcons[year]}
                </TimelineDot>
                <AnimatedBox
                  animation={index % 2 === 0 ? 'fadeInLeft' : 'fadeInRight'}
                  delay={`${index * 0.15}s`}
                  sx={{ width: '100%' }}
                >
                  <TimelineContent>
                    <YearBadge>{year}</YearBadge>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        mb: 1.5,
                        color: 'text.primary',
                      }}
                    >
                      {t(`about.journey.milestones.${year}.title`)}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'text.secondary',
                        lineHeight: 1.7,
                      }}
                    >
                      {t(`about.journey.milestones.${year}.description`)}
                    </Typography>
                  </TimelineContent>
                </AnimatedBox>
              </TimelineItem>
            ))}
          </TimelineContainer>
        </Container>
      </Section>

      {/* Mission & Vision */}
      <Section>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <AnimatedBox animation="fadeInLeft" sx={{ height: '100%' }}>
                <MissionVisionCard>
                  <CardContent sx={{ p: 4 }}>
                    <IconWrapper>
                      <RocketLaunchIcon />
                    </IconWrapper>
                    <Typography
                      variant="h4"
                      sx={{ mb: 2, fontWeight: 700 }}
                    >
                      {t('about.mission.title')}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ color: 'text.secondary', lineHeight: 1.8 }}
                    >
                      {t('about.mission.description')}
                    </Typography>
                  </CardContent>
                </MissionVisionCard>
              </AnimatedBox>
            </Grid>

            <Grid item xs={12} md={6}>
              <AnimatedBox animation="fadeInRight" sx={{ height: '100%' }}>
                <MissionVisionCard>
                  <CardContent sx={{ p: 4 }}>
                    <IconWrapper>
                      <VisibilityIcon />
                    </IconWrapper>
                    <Typography
                      variant="h4"
                      sx={{ mb: 2, fontWeight: 700 }}
                    >
                      {t('about.vision.title')}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ color: 'text.secondary', lineHeight: 1.8 }}
                    >
                      {t('about.vision.description')}
                    </Typography>
                  </CardContent>
                </MissionVisionCard>
              </AnimatedBox>
            </Grid>
          </Grid>
        </Container>
      </Section>

      {/* Technology-Driven Culture */}
      <Section>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <AnimatedBox animation="fadeInLeft">
                <IconWrapper sx={{ width: 80, height: 80 }}>
                  <PsychologyIcon sx={{ fontSize: '36px !important' }} />
                </IconWrapper>
                <Typography variant="h3" sx={{ mb: 3, fontWeight: 700 }}>
                  {t('about.culture.title')}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.secondary',
                    lineHeight: 1.8,
                    fontSize: '1.125rem',
                  }}
                >
                  {t('about.culture.description')}
                </Typography>
              </AnimatedBox>
            </Grid>
            <Grid item xs={12} md={6}>
              <AnimatedBox animation="fadeInRight">
                <Box
                  sx={{
                    background: (theme) => theme.palette.accent.gradient,
                    borderRadius: '24px',
                    p: 6,
                    position: 'relative',
                    overflow: 'hidden',

                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: '-50%',
                      right: '-30%',
                      width: '300px',
                      height: '300px',
                      borderRadius: '50%',
                      background: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      color: '#FFFFFF',
                      fontWeight: 800,
                      mb: 1,
                      position: 'relative',
                      zIndex: 1,
                    }}
                  >
                    8+
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.9)',
                      position: 'relative',
                      zIndex: 1,
                    }}
                  >
                    Years of digital transformation expertise
                  </Typography>
                </Box>
              </AnimatedBox>
            </Grid>
          </Grid>
        </Container>
      </Section>

      {/* Our Values */}
      <Section variant="gradient">
        <Container maxWidth="lg">
          <SectionTitle
            title={t('about.values.title')}
            light
          />

          <Grid container spacing={3}>
            {values.map((value, index) => (
              <Grid item xs={6} md={3} key={value.key}>
                <AnimatedBox animation="fadeInUp" delay={`${index * 0.1}s`}>
                  <ValueCard>
                    <ValueIcon>{value.icon}</ValueIcon>
                    <Typography
                      variant="h6"
                      sx={{ color: '#FFFFFF', fontWeight: 600 }}
                    >
                      {t(`about.values.${value.key}`)}
                    </Typography>
                  </ValueCard>
                </AnimatedBox>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Section>
    </>
  );
}
